// Post-login chrome: HLPT logo, employee identity, tab nav, settings,
// switch-user, and (for multi-base users) a roster toggle. The active
// tab renders inside <main>. State for tab selection lives here; timer
// state belongs to TimeTracker.
//
// The base toggle appears when employee.bases.length > 1 — e.g. Guest
// Support staff who also see Agency Support tasks. Clicking it flips
// the active base; TimeTracker is keyed by activeBaseId so it remounts
// and refetches against the new base.

import { useEffect, useState } from 'react';
import SettingsMenu from './SettingsMenu.jsx';
import TimeTracker from './TimeTracker.jsx';
import AiPlaceholder from './AiPlaceholder.jsx';
import OrgTab from './OrgTab.jsx';
import ConfirmModal from './ConfirmModal.jsx';
import { getSetting, onSettingsChange } from '../lib/settings.js';
import { toggleTheme } from '../lib/theme.js';
import { api } from '../lib/api.js';

const STATIC_TABS = [
  { id: 'tracker', label: 'Time Tracker', enabled: true, requiresTeamBase: true },
  { id: 'team',    label: 'Team',         enabled: true, requiresOrgScope: true },
  { id: 'ai',      label: 'AI',           enabled: false, badge: 'Coming soon' },
];

export default function AppShell({ employee, activeBaseId, onSwitchBase, onLogout }) {
  const [activeTab, setActiveTab] = useState('tracker');
  const [logoutPrompt, setLogoutPrompt] = useState(false);
  // Subscribe to theme changes so the top-bar toggle button shows the
  // correct icon (sun in dark mode → "switch to light"; moon in light
  // mode → "switch to dark"). The actual application of the theme is
  // handled by theme.js's onSettingsChange subscriber from initTheme.
  const [theme, setTheme] = useState(() => getSetting('theme'));
  useEffect(() => onSettingsChange(s => setTheme(s.theme)), []);

  // Org/Utilization tab is gated on `canSeeOrg` from /api/org-me. We
  // fetch this once per session — the response is cached server-side
  // (HR directory has a 1h TTL) and client-side ETag-ish via the
  // browser. Errors degrade silently to "no Team tab" (no flash).
  const [orgMe, setOrgMe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!employee?.id) return;
    api.orgMe(employee.id)
      .then(d => { if (!cancelled) setOrgMe(d); })
      .catch(() => { if (!cancelled) setOrgMe({ canSeeOrg: false }); });
    return () => { cancelled = true; };
  }, [employee?.id]);

  const bases = Array.isArray(employee?.bases) ? employee.bases : [];
  const hasTeamBase = bases.length > 0;
  const activeBase = bases.find(b => b.baseId === activeBaseId) || bases[0];

  // Filter tabs by capability:
  //   - Time Tracker requires at least one team base (HR-only execs like
  //     Phil don't track tasks here)
  //   - Team requires org scope (≥1 report or executive)
  //   - AI is always rendered but always disabled (Coming soon)
  // We don't filter on `requiresOrgScope` until orgMe loads (avoids a
  // "tab appears" flicker for users who DO have scope).
  const tabs = STATIC_TABS.filter(t => {
    if (t.requiresTeamBase && !hasTeamBase) return false;
    if (t.requiresOrgScope && orgMe?.canSeeOrg !== true) return false;
    return true;
  });

  // If the active tab disappears (e.g. user lost org scope, or their
  // bases array shrunk to zero), pick the first enabled tab in the
  // visible set. Order of preference is tracker → team → ai.
  useEffect(() => {
    const visibleIds = tabs.filter(t => t.enabled).map(t => t.id);
    if (visibleIds.length && !visibleIds.includes(activeTab)) {
      setActiveTab(visibleIds[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTeamBase, orgMe?.canSeeOrg]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-bg/90 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-md bg-accent flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
              H
            </div>
            <div className="min-w-0 hidden sm:block">
              <div className="text-sm font-semibold tracking-tight truncate">HLPT Co-Pilot</div>
              <div className="text-[11px] text-muted truncate">
                {employee?.name} · {activeBase?.baseName || employee?.team}
              </div>
            </div>
          </div>

          <nav className="flex items-center gap-1">
            {tabs.map(t => (
              <button
                key={t.id}
                disabled={!t.enabled}
                onClick={() => t.enabled && setActiveTab(t.id)}
                className={[
                  'px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors',
                  t.enabled && activeTab === t.id
                    ? 'bg-panel2 text-text'
                    : t.enabled
                      ? 'text-muted hover:text-text hover:bg-panel2'
                      : 'text-muted cursor-not-allowed',
                ].join(' ')}
              >
                {t.label}
                {t.badge && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-panel border border-border text-muted uppercase tracking-wider">
                    {t.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {bases.length > 1 && (
              <BaseToggle
                bases={bases}
                activeBaseId={activeBase?.baseId}
                onSwitch={onSwitchBase}
              />
            )}
            {/* Theme toggle promoted out of SettingsMenu — single-click
                affordance in the top bar. Tooltip describes the
                destination ("switch to light/dark") not the current. */}
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
              className="p-1.5 rounded-md hover:bg-panel border border-transparent hover:border-border transition-colors text-muted hover:text-text"
            >
              {theme === 'dark' ? (
                // Sun (= "click to go light")
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              ) : (
                // Moon (= "click to go dark")
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <SettingsMenu employee={employee} />
            <button
              onClick={() => setLogoutPrompt(true)}
              className="text-xs text-muted hover:text-text px-2 py-1 rounded-md hover:bg-panel"
              title="Sign out"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {logoutPrompt && (
        <ConfirmModal
          title="Sign out?"
          message="Any running timer will be paused before you're signed out. You can sign back in any time."
          confirmLabel="Sign out"
          cancelLabel="Stay signed in"
          danger={false}
          onConfirm={async () => {
            setLogoutPrompt(false);
            await onLogout();
          }}
          onCancel={() => setLogoutPrompt(false)}
        />
      )}

      <main className="max-w-6xl mx-auto px-4 py-5">
        {activeTab === 'tracker' && hasTeamBase && (
          // Key by activeBase so a roster switch fully remounts the
          // tracker — drops any cached queue/tasks/stats and refetches.
          // Spread activeBase last so its uid/baseId/baseName/formUrls
          // override the primary identity on the employee record.
          <TimeTracker
            key={activeBase?.baseId || 'default'}
            employee={{
              ...employee,
              id: activeBase?.uid || employee.id,
              baseId: activeBase?.baseId || employee.baseId,
              baseName: activeBase?.baseName || '',
              taskFormUrl: activeBase?.taskFormUrl || '',
              subtaskFormUrl: activeBase?.subtaskFormUrl || '',
            }}
          />
        )}
        {activeTab === 'team' && orgMe?.canSeeOrg && (
          // OrgTab uses the PRIMARY uid (employee.id) — not the active
          // base's uid — because the org view is independent of which
          // team-base the user has selected. For HR-only users (Phil),
          // employee.id IS the HR record id.
          <OrgTab viewerUid={employee.id} viewerName={employee.name} orgMe={orgMe} />
        )}
        {activeTab === 'ai' && <AiPlaceholder />}
      </main>
    </div>
  );
}

function BaseToggle({ bases, activeBaseId, onSwitch }) {
  return (
    <div className="flex items-center rounded-md border border-border bg-panel p-0.5" role="group" aria-label="Switch roster">
      {bases.map(b => {
        const active = b.baseId === activeBaseId;
        return (
          <button
            key={b.baseId}
            onClick={() => !active && onSwitch(b.baseId)}
            className={[
              'px-2.5 py-1 text-xs rounded transition-colors',
              active
                ? 'bg-accent text-white font-medium'
                : 'text-muted hover:text-text hover:bg-panel2',
            ].join(' ')}
            title={active ? `Currently working in ${b.baseName}` : `Switch to ${b.baseName}`}
          >
            {b.baseName || b.baseId}
          </button>
        );
      })}
    </div>
  );
}
